'use strict';
/* ==========================================================================
   levels/title.js — the title: a nursery greenhouse as an antique botanical plate (DESIGN.md §2).
   Aged paper with a plate border, the greenhouse (IMG.greenhouse, else glass panes on a white frame), pea vines
   climbing both edges, the lesson name on kraft seed-packet labels hung on garden twine (woodtype letters), the
   subtitle in hand lettering, a potting bench with pods and a seed packet, Lily (left) and Mr. Mendoza (right)
   rising in, ▶ Play → the map. On a phone held upright the controls move to the portrait dock (Dock.title) and the
   stage keeps only the art. Levels.title.loading() draws the "sowing seeds…" screen while assets load (no textures
   yet: plain fills + ink only).
   Conventions (shared/engine/v2/README.md): draw through g, a pure function of time (T, this.enteredAt), sizes of
   tappable things through UI.button / UI.tap, no per-frame shadowOn() (core-local/plate.js pieces are cached).
   ========================================================================== */
(function () {
  const PLAY = { x: 960, y: 972 };
  const PL = () => window.Plate;

  /* the title on 1–3 labels: balanced by length (the longest line as short as possible) */
  function titleLines(text) {
    const words = String(text).toUpperCase().split(/\s+/).filter(Boolean);
    if (words.join(' ').length <= 16) return [words.join(' ')];
    const n = words.join(' ').length > 28 ? 3 : 2;
    let best = null;
    const join = (a, b) => words.slice(a, b).join(' ');
    // the longest line as short as possible, then the most even (WHERE DID THOSE / BLUE EYES / COME FROM?)
    const score = L => { const m = Math.max(...L.map(s => s.length)), mean = L.reduce((a, s) => a + s.length, 0) / L.length; return m * 1000 + L.reduce((a, s) => a + (s.length - mean) ** 2, 0) - L[0].length * 0.01; };   // ties: the longer first line
    const consider = L => { const sc = score(L); if (!best || sc < best.sc) best = { sc, L }; };
    if (n === 2) for (let i = 1; i < words.length; i++) consider([join(0, i), join(i)]);
    else for (let i = 1; i < words.length - 1; i++) for (let j = i + 1; j < words.length; j++) consider([join(0, i), join(i, j), join(j)]);
    return best ? best.L : [words.join(' ')];
  }

  function backdrop(t0) {
    const P = PL(), C = P.C;
    P.backdrop(t0);
    P.greenhouse(960, 560, 1180, 800, { seed: 7 });
    // the potting bench along the floor
    paperRect(960, 1030, 2000, 120, C.wood, { seed: 21, torn: false, depth: 1.2, boil: false });
    ink([[0, 985], [1920, 988]], { w: 3, color: 'rgba(60,35,15,0.5)', seed: 22, passes: 1, step: 80 });
    // pea vines up both edges
    P.vine(130, 1000, 820, { seed: 11, side: 1, s: pop(T, t0 + 0.2, 0.5, 1.6) });
    P.vine(1790, 1000, 800, { seed: 13, side: -1, s: pop(T, t0 + 0.3, 0.5, 1.6) });
    // pods and a seed packet on the bench
    const pod = (x, y, rot, sd, at) => {
      const s = pop(T, t0 + at, 0.35, 2.2); if (s <= 0) return;
      if (IMG.pea_pod) cut('pea_pod', x, y, 90, { rot, s, seed: sd, depth: 0.8 });
      else { g.save(); g.translate(x, y); g.rotate(rot); P.pheno('pod', 'inflated', 0, 0, 34, { seed: sd, s }); g.restore(); }
    };
    pod(400, 1004, 0.1, 43, 1.5); pod(480, 1000, -0.2, 41, 1.6); pod(555, 1012, 0.25, 42, 1.7);   // the bench decor stays left of ▶ Play (its hint is on the right)
    const sp = pop(T, t0 + 1.8, 0.35, 2);
    if (sp > 0) {
      if (IMG.seed_packet) cut('seed_packet', 660, 990, 150, { rot: 0.12, s: sp, seed: 44, depth: 1 });
      else P.packet(660, 975, 110, 150, { title: 'PEAS', color: C.leaf, seed: 44, rot: 0.12, s: sp, pic: { trait: 'seedColour', value: 'yellow' } });
    }
  }

  class Title extends Scene {
    enter() { this.started = false; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a paper tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {}, P = PL(), C = P.C;
      backdrop(t0);

      // the title: kraft labels on twine, swinging a touch
      const lines = titleLines(L.title || 'WHERE DID THOSE BLUE EYES COME FROM?');
      const size = Math.min(84, ...lines.map(l => 900 / (P.woodWidth(l, 100) / 100) - 8));
      const y0 = lines.length > 2 ? 150 : 200, dy = size * 1.95;
      P.twine(380, y0 - size * 1.3, 1540, y0 - size * 1.3, { sag: 26, seed: 71 });
      lines.forEach((l, i) => {
        const y = y0 + i * dy, s = pop(T, t0 + 0.3 + i * 0.3, 0.45, 1.8), sw = Math.sin(T * 1.2 + i * 1.9) * 0.008;
        if (i > 0) { ink([[960 - 260, y - dy + size * 0.8], [960 - 250, y - size * 0.8]], { w: 3, color: C.twine, seed: 72 + i, passes: 1, step: 20 }); ink([[960 + 250, y - dy + size * 0.8], [960 + 262, y - size * 0.8]], { w: 3, color: C.twine, seed: 75 + i, passes: 1, step: 20 }); }
        P.label(l, 960, y, Math.round(size), { t0: t0 + 0.35 + i * 0.3, seed: 101 + i * 17, rot: [-0.012, 0.01, -0.008][i % 3] + sw, s });
      });
      const ySub = y0 + (lines.length - 1) * dy + size * 1.55;
      // a cream scrap under the subtitle + the plate caption: over the greenhouse engraving the lettering was hard to read
      paperRect(960, ySub + 30, 960, 140, C.label, { seed: 91, depth: 0.9, boil: false, rot: -0.01, s: pop(T, t0 + 1.2, 0.35, 1.8) });
      hand(L.subtitle || '', 960, ySub, 56, t0 + 1.3, { dur: 0.5, color: C.leafDark, rot: -0.012 });
      g.save(); g.font = `italic 30px 'Playfair Display'`; g.textAlign = 'center'; g.fillStyle = 'rgba(43,36,32,0.6)';
      g.globalAlpha *= clamp((T - t0 - 1.6) / 0.4); g.fillText('Pl. I · Pisum sativum, after Mendel (1865)', 960, ySub + 58); g.restore();

      // Lily (left) and Mr. Mendoza (right) rise in from below
      const riseL = E.outBack(P_(t0 + 0.5, t0 + 1.0), 1.4), riseM = E.outBack(P_(t0 + 0.7, t0 + 1.2), 1.4);
      Puppet.draw('lily', 260, 1080 + (1 - riseL) * 600, 680, { facing: 1, t: T });
      Puppet.draw('mendoza', 1660, 1080 + (1 - riseM) * 600, 760, { facing: -1, t: T });

      // portrait phone: the controls are in the dock (core/dock.js Dock.title); the stage keeps only the art
      if (UI.docked()) { Dock.title({ onStart: () => this.go(), onContinue: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 380, h: 116, color: C.leafDark, font: "'Luckiest Guy'", size: 64, rot: -0.015 })) this.go();
      if (t > 2.4) hand(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x, PLAY.y + 84 + (UI.tap(116) - 116) / 2, UI.text(30), t0 + 2.4, { color: 'rgba(250,240,215,0.9)', seed: 61 });   // on the bench, under the button
    }
  }
  const P_ = (a, b) => P(T, a, b);
  Title.PLAY = PLAY;                         // tools/integration.js clicks it

  /* loading screen: no textures yet → plain paper, a row of seeds dropping into a furrow one by one */
  Title.loading = function () {
    g.fillStyle = '#efe4c8'; g.fillRect(0, 0, Game.W, Game.H);
    ink([[520, 560], [1400, 560]], { w: 8, color: '#6a4322', seed: 7, passes: 1, step: 40 });
    const n = 9, k = Math.floor((T * 5) % (n + 4));
    for (let i = 0; i < Math.min(k, n); i++) {
      g.beginPath(); g.arc(580 + i * 95, 540, 18, 0, TAU); g.fillStyle = i % 4 === 3 ? '#7fae4b' : '#e9c53d'; g.fill();
      g.lineWidth = 3; g.strokeStyle = '#2b2420'; g.stroke();
    }
    hand('sowing seeds…', 960, 670, 64, 0, { dur: 0.01, seed: 5, color: '#2b2420' });
  };

  Levels.title = Title;
})();
